import { inclusiveEnd } from './dates';

const TIERS = ['mid', 'premium'];

export const formatCredits = amount => amount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const computeOrderPrice = (conditionalPrice, screeningDate, output, tier) => {
    const tierPrices = conditionalPrice?.[tier] || [];
    if (!tierPrices.length) {
        return output === 'number' ? null : 'N/A';
    }

    const targetDate = new Date(screeningDate);

    // Find the price period that contains the screening date
    for (const condition of tierPrices) {
        const fromDate = new Date(condition.fromDate);
        const toDate = new Date(condition.toDate);

        if (targetDate >= fromDate && targetDate <= toDate) {
            return output === 'number' ? condition.price : formatCredits(condition.price);
        }
    }

    // No valid price period found for this date
    return output === 'number' ? null : 'N/A';
};

export const getPriceRange = (conditionalPrice, tier) => {
    const tierPrices = conditionalPrice?.[tier] || [];
    if (!tierPrices.length) {
        return { min: null, max: null };
    }

    const prices = tierPrices.map(c => c.price);
    return { min: Math.min(...prices), max: Math.max(...prices) };
};

// What a site pays: the version's price table for the site's territory
// (organization.country), falling back to the version default for any tier the
// territory has no bands for. Mirrors bac-server/src/utils/pricing.js.
export const getSitePriceTable = (version, organization) => {
    const country = organization?.country;
    const territory = country ? version?.territoryPrices?.find(t => t.countryCode === country) : null;
    const table = {};
    TIERS.forEach(tier => {
        const own = territory?.[tier] || [];
        table[tier] = own.length ? own : (version?.conditionalPrice?.[tier] || []);
    });
    return table;
};

export const getSitePrice = (version, organization, screeningDate, output) => (
    computeOrderPrice(getSitePriceTable(version, organization), screeningDate, output, organization?.tier)
);

export const getSitePriceRange = (version, organization) => (
    getPriceRange(getSitePriceTable(version, organization), organization?.tier)
);

// Distributor rows ({ tier[], daysBeforeRelease, daysAfterRelease, credits })
// dated from the release date, for the tiers the version is offered to.
export const buildPriceTable = (rows, releaseDate, tiers) => {
    const table = { mid: [], premium: [] };
    (tiers || []).forEach(tier => {
        table[tier] = (rows || []).filter(row => row.tier.includes(tier)).map(row => {
            const fromDate = new Date(releaseDate);
            fromDate.setDate(fromDate.getDate() + row.daysBeforeRelease);
            // daysAfterRelease is the last day the band applies to, so the
            // band has to run through the end of it — the next band starts
            // on daysAfterRelease + 1.
            const toDate = new Date(releaseDate);
            toDate.setDate(toDate.getDate() + row.daysAfterRelease);

            return { fromDate, toDate: inclusiveEnd(toDate), price: row.credits };
        });
    });
    return table;
};
